import { useState, useEffect, useCallback } from 'react';

export interface UsePayerGroupFilterOptions {
  /** Unique key for session storage persistence */
  storageKey: string;
}

export interface UsePayerGroupFilterReturn {
  /** Current selected payer groups */
  value: string[];
  /** Whether any filter is active */
  hasFilter: boolean;
  /** Set the selected payer groups */
  setValue: (groups: string[]) => void;
  /** Clear all selected payer groups */
  clear: () => void;
}

// Helper to get session storage key for payer group filters
const getPayerGroupFilterStorageKey = (key: string) => `payer-group-filter-${key}`;

// Helper to parse stored payer groups from session storage
const getStoredPayerGroups = (key: string): string[] => {
  try {
    const stored = sessionStorage.getItem(getPayerGroupFilterStorageKey(key));
    if (stored) {
      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    }
  } catch {
    // Ignore parse errors
  }
  return [];
};

// Helper to save payer groups to session storage
const savePayerGroups = (key: string, groups: string[]) => {
  sessionStorage.setItem(
    getPayerGroupFilterStorageKey(key),
    JSON.stringify(groups)
  );
};

/**
 * Hook for managing payer group filter state with session persistence.
 * 
 * @example
 * ```tsx
 * const payerGroupFilter = usePayerGroupFilter({ storageKey: `writeoff-payers-${centerId}` });
 * 
 * // Use in query params
 * const queryParams = {
 *   centerId,
 *   ...(payerGroupFilter.value.length > 0 && { payerGroups: payerGroupFilter.value }),
 * };
 * 
 * // Render the filter component
 * <PayerGroupFilter filter={payerGroupFilter} />
 * ```
 */
export function usePayerGroupFilter({ storageKey }: UsePayerGroupFilterOptions): UsePayerGroupFilterReturn {
  const [selectedGroups, setSelectedGroups] = useState<string[]>([]);

  // Load stored payer groups when storage key changes
  useEffect(() => {
    if (storageKey) {
      const stored = getStoredPayerGroups(storageKey);
      setSelectedGroups(stored);
    }
  }, [storageKey]);

  const setValue = useCallback((groups: string[]) => {
    setSelectedGroups(groups);
    savePayerGroups(storageKey, groups);
  }, [storageKey]);

  const clear = useCallback(() => {
    setSelectedGroups([]);
    savePayerGroups(storageKey, []);
  }, [storageKey]);

  return {
    value: selectedGroups,
    hasFilter: selectedGroups.length > 0,
    setValue,
    clear,
  };
}
